import { ALL_TECHS, Cat, KINDS, kindIndex } from '../gameplay/registry';
import { AGE_UP_REQUIREMENTS } from '../gameplay/technologies';
import { C, Res } from '../gameplay/types';
import type { Command } from '../engine/simulation/commands';
import { FX_ONE, FX_SHIFT } from '../engine/simulation/fixed';
import { Rng, hash2 } from '../engine/simulation/rng';
import { canPlace } from '../engine/simulation/systems/construction';
import { CAPTURE_DEFENCE_RADIUS, canCapture } from '../engine/simulation/systems/capture';
import { gatherableBy, isFarm, resourceOf } from '../engine/simulation/systems/economy';
import { canAfford, researchableAt, techAvailability, trainableAt } from '../engine/simulation/systems/production';
import { Order, UState, type World } from '../engine/simulation/world';
import type { Simulation } from '../engine/simulation/simulation';

export type AIDifficulty = 'beginner' | 'standard' | 'hard' | 'expert';
export type AIPersonality = 'aggressive' | 'defensive' | 'economic' | 'technological' | 'naval' | 'expansionist' | 'balanced';

interface DifficultyProfile {
  /** Ticks between strategic decisions. */
  interval: number;
  maxVillagers: number;
  /** Army size before first attack. */
  attackAt: number;
  /** Uses focus fire / retreat micro. */
  micro: boolean;
  /** Adapts army to counter observed enemy composition. */
  counters: boolean;
  /** Chance per decision of skipping (models slower reactions). */
  sloppiness: number;
  maxAge: number;
  /** Minimum seconds between attack waves. */
  waveGap: number;
}

// Tuned down ~50% from the first release after playtesting (slower decisions,
// smaller economies, more hesitation, longer gaps between attack waves).
const DIFFICULTY: Record<AIDifficulty, DifficultyProfile> = {
  beginner: { interval: 200, maxVillagers: 10, attackAt: 10, micro: false, counters: false, sloppiness: 65, maxAge: 1, waveGap: 480 },
  standard: { interval: 120, maxVillagers: 18, attackAt: 10, micro: false, counters: true, sloppiness: 45, maxAge: 2, waveGap: 300 },
  hard: { interval: 64, maxVillagers: 28, attackAt: 14, micro: true, counters: true, sloppiness: 30, maxAge: 2, waveGap: 200 },
  expert: { interval: 32, maxVillagers: 40, attackAt: 16, micro: true, counters: true, sloppiness: 15, maxAge: 3, waveGap: 120 },
};

interface PersonalityProfile {
  /** Multiplier (%) on attack threshold. */
  aggression: number;
  /** Desired number of towers. */
  towers: number;
  /** Extra villagers before ageing (%). */
  ecoBias: number;
  /** Research eagerness (%). */
  tech: number;
  /** Extra town centres desired. */
  expand: number;
}

const PERSONALITY: Record<AIPersonality, PersonalityProfile> = {
  aggressive: { aggression: 75, towers: 0, ecoBias: 95, tech: 70, expand: 0 },
  defensive: { aggression: 160, towers: 3, ecoBias: 100, tech: 100, expand: 0 },
  economic: { aggression: 140, towers: 1, ecoBias: 130, tech: 120, expand: 1 },
  technological: { aggression: 130, towers: 1, ecoBias: 110, tech: 160, expand: 0 },
  naval: { aggression: 110, towers: 1, ecoBias: 100, tech: 100, expand: 0 },
  expansionist: { aggression: 110, towers: 1, ecoBias: 110, tech: 90, expand: 2 },
  balanced: { aggression: 100, towers: 1, ecoBias: 100, tech: 100, expand: 0 },
};

/** Rules v7: the furthest age each difficulty aims for (before v7, DifficultyProfile.maxAge). */
const MAX_AGE_V7: Record<AIDifficulty, number> = { beginner: 2, standard: 4, hard: 4, expert: 4 };

/**
 * Rules v7: a computer player's current plan. It starts on 'standard' and, after losing a battle (an
 * attack wave crushed, or most of its army lost defending), switches to another to try something new.
 */
export type Strategy = 'standard' | 'counter' | 'boom' | 'turtle' | 'mass';
interface StrategyMods {
  /** Army size before attacking (%). */
  armyPct: number;
  /** Time between attack waves (%). */
  waveGapPct: number;
  /** Extra villagers (% of the usual target). */
  ecoPlus: number;
  /** Extra towers. */
  towersPlus: number;
  /** Weight on units that counter what the enemy fields (%). */
  counterPct: number;
  /** Preference for siege engines in the army. */
  siegePlus: number;
  /** Extra town centres. */
  expandPlus: number;
}
const STRATEGY: Record<Strategy, StrategyMods> = {
  standard: { armyPct: 100, waveGapPct: 100, ecoPlus: 0, towersPlus: 0, counterPct: 100, siegePlus: 0, expandPlus: 0 },
  // Build what beats the units that won the last fight.
  counter: { armyPct: 110, waveGapPct: 100, ecoPlus: 0, towersPlus: 0, counterPct: 250, siegePlus: 5, expandPlus: 0 },
  // Grow the economy first, attack later with more.
  boom: { armyPct: 130, waveGapPct: 180, ecoPlus: 25, towersPlus: 1, counterPct: 100, siegePlus: 0, expandPlus: 1 },
  // Dig in: towers, a bigger standing army, slower waves.
  turtle: { armyPct: 150, waveGapPct: 160, ecoPlus: 10, towersPlus: 3, counterPct: 120, siegePlus: 0, expandPlus: 0 },
  // Wait for an overwhelming army with siege.
  mass: { armyPct: 200, waveGapPct: 140, ecoPlus: 10, towersPlus: 0, counterPct: 120, siegePlus: 15, expandPlus: 0 },
};

interface KnownBuilding {
  id: number;
  x: number;
  y: number;
  owner: number;
}

/**
 * Hierarchical computer opponent.
 *  - Strategic layer: economy targets, build order, age advancement, research, army composition.
 *  - Operational layer: scouting, defence, attack waves, target selection.
 *  - Tactical layer: focus fire and retreat of wounded units (higher difficulties).
 * The AI only acts through the same validated commands as human players and only
 * uses enemy information visible in its own fog of war.
 */
export class AIController {
  readonly player: number;
  readonly difficulty: AIDifficulty;
  readonly personality: AIPersonality;
  private rng: Rng;
  private seq = 1_000_000;
  private d: DifficultyProfile;
  private pp: PersonalityProfile;
  private known = new Map<number, KnownBuilding>();
  private enemySeen = { cavalry: 0, archer: 0, infantry: 0, siege: 0, ships: 0 };
  private attacking = false;
  private lastAttackTick = 0;
  private scoutIdx = 0;
  private attackWave = 0;
  /** Rules v4: the player the current attack wave is aimed at (-1 = choose at the next wave). */
  private waveTarget = -1;
  /** Rules v7: current plan, battles lost so far, and the army size when a defence began. */
  strategy: Strategy = 'standard';
  /** Set at each decision: saving for and building a Wonder (rules v7, Wonder victory on). */
  private wonderWanted = false;
  /** Set at each decision (rules v7): an enemy player whose Wonder is standing, or -1. */
  private enemyWonder = -1;
  private battlesLost = 0;
  private defencePeak = 0;
  private foodWanted = 0;
  /** Rules v8: whether reaching each enemy player's start needs a ship (land route blocked or far longer). */
  private seaRoute = new Map<number, boolean>();
  /** Rules v8: whether a ship from our Dock can reach the coast near each enemy player's start. */
  private seaReach = new Map<number, boolean>();
  /** Rules v8: a Caravelle carrying part of an attack wave (ship id, stage, stage start, landing point). */
  private ferry: { ship: number; stage: 0 | 1 | 2; since: number; x: number; y: number } | null = null;
  /** Tick of the next decision; randomised so the AI hesitates like a person. */
  private nextThink = 0;

  constructor(player: number, difficulty: AIDifficulty, personality: AIPersonality, seed: number) {
    this.player = player;
    this.difficulty = difficulty;
    this.personality = personality;
    this.rng = new Rng(seed);
    this.d = DIFFICULTY[difficulty];
    this.pp = PERSONALITY[personality];
  }

  /** Serialisable state for save games. */
  toJSON(): unknown {
    return {
      player: this.player, difficulty: this.difficulty, personality: this.personality, rng: this.rng.state, seq: this.seq,
      known: [...this.known.values()], enemySeen: this.enemySeen, attacking: this.attacking, lastAttackTick: this.lastAttackTick,
      scoutIdx: this.scoutIdx, attackWave: this.attackWave, nextThink: this.nextThink, waveTarget: this.waveTarget,
      strategy: this.strategy, battlesLost: this.battlesLost, defencePeak: this.defencePeak,
      seaRoute: [...this.seaRoute], seaReach: [...this.seaReach], ferry: this.ferry,
    };
  }

  static fromJSON(o: ReturnType<AIController['toJSON']>): AIController {
    const j = o as {
      player: number; difficulty: AIDifficulty; personality: AIPersonality; rng: number; seq: number; known: KnownBuilding[];
      enemySeen: AIController['enemySeen']; attacking: boolean; lastAttackTick: number; scoutIdx: number; attackWave: number; nextThink?: number;
      waveTarget?: number; strategy?: Strategy; battlesLost?: number; defencePeak?: number;
      seaRoute?: [number, boolean][]; seaReach?: [number, boolean][]; ferry?: AIController['ferry'];
    };
    const a = new AIController(j.player, j.difficulty, j.personality, 1);
    a.rng.state = j.rng;
    a.seq = j.seq;
    for (const k of j.known) a.known.set(k.id, k);
    a.enemySeen = j.enemySeen;
    a.attacking = j.attacking;
    a.lastAttackTick = j.lastAttackTick;
    a.scoutIdx = j.scoutIdx;
    a.attackWave = j.attackWave;
    a.nextThink = j.nextThink ?? 0;
    a.waveTarget = j.waveTarget ?? -1;
    a.strategy = j.strategy ?? 'standard';
    a.battlesLost = j.battlesLost ?? 0;
    a.defencePeak = j.defencePeak ?? 0;
    a.seaRoute = new Map(j.seaRoute ?? []);
    a.seaReach = new Map(j.seaReach ?? []);
    a.ferry = j.ferry ?? null;
    return a;
  }

  /** Furthest age this AI advances to under the rules in force. */
  private maxAge(w: World): number {
    return w.rules >= 7 ? MAX_AGE_V7[this.difficulty] : this.d.maxAge;
  }

  /** The current plan's modifiers (always the standard plan before rules v7). */
  private mods(w: World): StrategyMods {
    return STRATEGY[w.rules >= 7 ? this.strategy : 'standard'];
  }

  /**
   * A villager-count threshold: before rules v7 the fixed number; from v7 a fraction of this AI's own
   * villager limit (fixed numbers above the limit meant some buildings were never built).
   */
  private vil(w: World, fixed: number, frac: number): number {
    return w.rules >= 7 ? Math.trunc(this.villagerTarget(w) * frac) : fixed;
  }

  /** How many villagers the AI keeps. From rules v7 a few more each age past the Feudal Age. */
  private villagerTarget(w: World): number {
    const base = Math.trunc((this.d.maxVillagers * (this.pp.ecoBias + this.mods(w).ecoPlus)) / 100);
    return w.rules >= 7 ? base + 4 * Math.max(0, w.players[this.player].age - 1) : base;
  }

  /** Rules v7, with a Wonder victory on: this AI wants a Wonder and has none standing or rising. */
  private wantsWonder(w: World, sim: Simulation, v: ReturnType<AIController['view']>): boolean {
    if (w.rules < 7 || !sim.config.victory.conditions.includes('wonder') || this.difficulty === 'beginner') return false;
    if (w.players[this.player].age < KINDS[kindIndex('wonder')].building!.age) return false;
    return (v.counts.get('wonder') ?? 0) === 0;
  }

  /**
   * An enemy player with a Wonder standing (Wonder timers are shown to everyone), nearest first; -1 if
   * none. The Wonder must fall before its timer runs out.
   */
  private enemyWithWonder(w: World, sim: Simulation): number {
    if (!sim.config.victory.conditions.includes('wonder')) return -1;
    for (const team of sim.victoryState.wonderSince.keys()) {
      for (const p of w.players) if (p.team === team && !p.defeated && w.areEnemies(this.player, p.id)) return p.id;
    }
    return -1;
  }

  /**
   * Rules v7: a battle was lost. Try another plan, chosen from those not just tried; after a crushing
   * defeat against one kind of unit, countering it is the first thought.
   */
  private rethink(w: World): void {
    if (w.rules < 7) return;
    this.battlesLost++;
    const e = this.enemySeen;
    const dominant = Math.max(e.cavalry, e.archer, e.infantry, e.siege);
    const total = e.cavalry + e.archer + e.infantry + e.siege;
    const options: Strategy[] = (['counter', 'boom', 'turtle', 'mass'] as Strategy[]).filter((s) => s !== this.strategy);
    if (this.strategy !== 'counter' && total > 0 && dominant * 2 > total && this.battlesLost % 2 === 1) this.strategy = 'counter';
    else this.strategy = options[hash2(this.battlesLost, this.player, w.tick + w.map.seed) % options.length];
    this.waveTarget = -1;
  }

  private issue(sim: Simulation, cmd: Command): void {
    sim.schedule({ tick: sim.world.tick + 1, player: this.player, seq: this.seq++, cmd });
  }

  update(sim: Simulation): void {
    const w = sim.world;
    const p = w.players[this.player];
    if (p.defeated || w.gameOver) return;
    if (w.tick < this.nextThink) return;
    // Next decision after the base interval plus a random pause; now and then a long distraction.
    const iv = this.d.interval;
    this.nextThink = w.tick + iv + this.rng.int(iv) + (this.rng.int(100) < 12 ? iv * (2 + this.rng.int(4)) : 0);
    if (this.d.sloppiness > 0 && this.rng.int(100) < this.d.sloppiness) return;

    const v = this.view(w);
    this.wonderWanted = this.wantsWonder(w, sim, v);
    this.enemyWonder = w.rules >= 7 ? this.enemyWithWonder(w, sim) : -1;
    this.observe(w);
    this.economy(sim, w, v);
    this.build(sim, w, v);
    this.research(sim, w, v);
    this.naval(sim, w, v);
    this.trade(sim, w, v);
    this.train(sim, w, v);
    this.operations(sim, w, v);
    if (w.rules >= 5) this.engineerWork(sim, w, v);
    if (w.rules >= 8) {
      this.townBell(sim, w, v);
      this.ferryWork(sim, w, v);
    }
    if (this.d.micro) this.tactics(sim, w, v);
  }

  /**
   * Rules v8: when raiders reach a Town Centre and outnumber the soldiers there, ring the bell (the
   * villagers around it shelter inside, adding arrows); once the raiders are gone, back to work.
   * Beginners never think of it.
   */
  private townBell(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    if (this.difficulty === 'beginner') return;
    const pid = this.player;
    const vis = w.visibility[pid];
    const r = 12 * FX_ONE;
    for (const tc of v.buildings) {
      if (KINDS[w.kind[tc]].key !== 'townCenter' || !w.built[tc]) continue;
      let foes = 0;
      let friends = 0;
      w.forUnitsInRadius(w.x[tc], w.y[tc], r, (o) => {
        const cl = KINDS[w.kind[o]].unit!.classes;
        if (!(cl & C.Military)) return;
        if (w.owner[o] === pid) friends++;
        else if (w.areEnemies(pid, w.owner[o]) && (!vis || vis[(w.y[o] >> FX_SHIFT) * w.size + (w.x[o] >> FX_SHIFT)] === 2)) foes++;
      });
      if (foes >= 3 && foes > friends) {
        const room = w.capacityOf(tc) - w.cargo[tc];
        const near = v.villagers
          .filter((s) => w.order[s] !== Order.Garrison && sq(w.x[s] - w.x[tc]) + sq(w.y[s] - w.y[tc]) <= sq(18 * FX_ONE))
          .sort((a, b) => sq(w.x[a] - w.x[tc]) + sq(w.y[a] - w.y[tc]) - sq(w.x[b] - w.x[tc]) - sq(w.y[b] - w.y[tc]) || a - b)
          .slice(0, room);
        if (near.length) this.issue(sim, { t: 'garrison', ids: near.map((s) => w.idOf(s)), target: w.idOf(tc) });
      } else if (foes === 0 && w.cargo[tc] > 0) {
        this.issue(sim, { t: 'unload', ids: [w.idOf(tc)], resume: true });
      }
    }
  }

  /**
   * Rules v8: whether our army must go by sea to reach a player's start: no land route at all (walled
   * in, say), or one more than half as long again as the straight line (plus 20 tiles). Worked out
   * once per player per attack wave.
   */
  private needsSea(w: World, v: ReturnType<AIController['view']>, player: number): boolean {
    const known = this.seaRoute.get(player);
    if (known !== undefined) return known;
    const st = w.map.starts[player];
    if (v.tc < 0 || !st) return false;
    const gx = (st.tx << FX_SHIFT) + 512;
    const gy = (st.ty << FX_SHIFT) + 512;
    const path = w.pathfinder.findPath(w.x[v.tc], w.y[v.tc] + 3 * FX_ONE, gx, gy, 60000);
    let len = 0;
    let px = w.x[v.tc];
    let py = w.y[v.tc] + 3 * FX_ONE;
    for (let i = 0; i < path.length; i += 2) {
      len += Math.sqrt(sq(path[i] - px) + sq(path[i + 1] - py));
      px = path[i];
      py = path[i + 1];
    }
    const straight = Math.sqrt(sq(gx - w.x[v.tc]) + sq(gy - w.y[v.tc]));
    const reached = path.length > 0 && sq(px - gx) + sq(py - gy) <= sq(6 * FX_ONE);
    const sea = !reached || len > straight * 1.5 + 20 * FX_ONE;
    this.seaRoute.set(player, sea);
    return sea;
  }

  /**
   * Rules v8: whether a ship from our Dock can sail near a player's start (within 20 tiles, or half
   * the distance from our Town Centre); the sea may not reach them at all. Worked out once per player.
   */
  private reachesBySea(w: World, v: ReturnType<AIController['view']>, player: number): boolean {
    const known = this.seaReach.get(player);
    if (known !== undefined) return known;
    const dock = v.buildings.find((b) => KINDS[w.kind[b]].key === 'dock' && w.built[b]);
    const st = w.map.starts[player];
    if (dock === undefined || !st) return false;
    const gx = (st.tx << FX_SHIFT) + 512;
    const gy = (st.ty << FX_SHIFT) + 512;
    let px = w.x[dock];
    let py = w.y[dock];
    // Long voyages need more than one search (each is limited); stop when one gets no further.
    for (let leg = 0; leg < 4; leg++) {
      const path = w.pathfinderWater.findPath(px, py, gx, gy, 60000);
      if (path.length < 2) break;
      const nx = path[path.length - 2];
      const ny = path[path.length - 1];
      if (sq(nx - px) + sq(ny - py) <= sq(2 * FX_ONE)) break;
      px = nx;
      py = ny;
    }
    // Worth it when the ship lands them within 20 tiles, or within half the distance from home.
    const home = v.tc >= 0 ? Math.sqrt(sq(gx - w.x[v.tc]) + sq(gy - w.y[v.tc])) : 0;
    const ok = Math.sqrt(sq(px - gx) + sq(py - gy)) <= Math.max(20 * FX_ONE, home / 2);
    this.seaReach.set(player, ok);
    return ok;
  }

  /**
   * Rules v8: from the Castle Age, a computer player with a Dock (above Beginner) lands a boatload of
   * each attack wave from the sea beside the target, while the rest march, wherever its ships can
   * reach that enemy's coast.
   */
  private seaLandings(w: World, v: ReturnType<AIController['view']>, player = -1): boolean {
    if (this.difficulty === 'beginner' || w.players[this.player].age < 2 || !(v.counts.get('dock') ?? 0)) return false;
    if (player >= 0) return this.reachesBySea(w, v, player);
    return w.players.some((p) => !p.defeated && w.areEnemies(this.player, p.id) && this.reachesBySea(w, v, p.id));
  }

  /** Rules v8: whether a transport is wanted: for sea landings, or an enemy cut off by land. */
  private wantsTransport(w: World, v: ReturnType<AIController['view']>): boolean {
    if (this.seaLandings(w, v)) return true;
    for (const p of w.players) if (!p.defeated && w.areEnemies(this.player, p.id) && this.needsSea(w, v, p.id) && this.reachesBySea(w, v, p.id)) return true;
    return false;
  }

  /**
   * Rules v8: put up to a Caravelle-load of an attack wave aboard, sail it to the target and land it.
   * Returns the units sent aboard (the rest of the wave goes by land).
   */
  private embark(sim: Simulation, w: World, v: ReturnType<AIController['view']>, army: number[], x: number, y: number): number[] {
    if (this.ferry || this.waveTarget < 0) return [];
    const t = this.waveTarget;
    if (!this.seaLandings(w, v, t) && !(this.needsSea(w, v, t) && this.reachesBySea(w, v, t))) return [];
    const ship = v.transports.find((s) => w.cargo[s] === 0 && w.order[s] === Order.None);
    if (ship === undefined) return [];
    const byDist = army.slice().sort((a, b) => sq(w.x[a] - w.x[ship]) + sq(w.y[a] - w.y[ship]) - sq(w.x[b] - w.x[ship]) - sq(w.y[b] - w.y[ship]) || a - b);
    const go: number[] = [];
    let room = w.capacityOf(ship);
    for (const s of byDist) {
      const n = w.placesFor(s, ship);
      if (n === 0 || n > room) continue;
      go.push(s);
      room -= n;
    }
    if (!go.length) return [];
    this.issue(sim, { t: 'garrison', ids: go.map((s) => w.idOf(s)), target: w.idOf(ship) });
    this.ferry = { ship: w.idOf(ship), stage: 0, since: w.tick, x, y };
    return go;
  }

  /** Rules v8: drive the ferry: wait for boarding (a minute at most), sail and land, come home. */
  private ferryWork(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const f = this.ferry;
    if (!f) return;
    const ship = w.resolve(f.ship);
    if (ship < 0) {
      this.ferry = null;
      return;
    }
    if (f.stage === 0) {
      // Give the boarding order time to take effect, then wait (a minute at most) for everyone.
      if (w.tick - f.since < 20 * 5) return;
      const boarding = v.military.filter((s) => w.order[s] === Order.Garrison && w.target[s] === f.ship);
      if (boarding.length && w.tick - f.since < 20 * 60) return;
      // Those who could not get aboard go by land.
      if (boarding.length) this.issue(sim, { t: 'move', ids: boarding.map((s) => w.idOf(s)), x: f.x, y: f.y, attackMove: true });
      if (!w.cargo[ship]) {
        this.ferry = null;
        return;
      }
      this.issue(sim, { t: 'unload', ids: [f.ship], x: f.x, y: f.y });
      f.stage = 1;
      f.since = w.tick;
    } else if (w.cargo[ship] === 0) {
      // Landed: the wave picks the troops up from here; the ship goes home.
      const dock = v.buildings.find((b) => KINDS[w.kind[b]].key === 'dock' && w.built[b]);
      if (dock !== undefined) this.issue(sim, { t: 'move', ids: [f.ship], x: w.x[dock], y: w.y[dock] });
      this.ferry = null;
    } else if (w.order[ship] === Order.None && w.tick - f.since > 20 * 3) {
      // Could not land where sent: try the nearest shore; failing that, sail home and land by the Dock.
      const dock = v.buildings.find((b) => KINDS[w.kind[b]].key === 'dock' && w.built[b]);
      if (f.stage === 1) {
        this.issue(sim, { t: 'unload', ids: [f.ship] });
        f.stage = 2;
      } else if (dock !== undefined && (f.x !== w.x[dock] || f.y !== w.y[dock])) {
        f.x = w.x[dock];
        f.y = w.y[dock];
        this.issue(sim, { t: 'unload', ids: [f.ship], x: f.x, y: f.y });
      } else this.ferry = null;
      f.since = w.tick;
    }
  }

  /**
   * Rules v5: from the Imperial Age, keep a couple of engineers. They repair badly damaged buildings
   * at home and commandeer enemy buildings the AI can see standing undefended (no enemy soldiers or
   * defences in sight within 8 tiles), as a person would after clearing an area.
   */
  private engineerWork(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    const want = this.difficulty === 'beginner' ? 0 : this.difficulty === 'expert' ? 3 : 2;
    const uni = v.buildings.find((b) => w.built[b] && KINDS[w.kind[b]].key === 'university');
    const ek = kindIndex('engineer');
    if (uni !== undefined && p.age >= 3 && v.engineers.length < want && !(w.queues[uni]?.length) && p.pop < p.popCap && canAfford(p, p.stats[ek].cost)) {
      this.issue(sim, { t: 'train', building: w.idOf(uni), kind: 'engineer', count: 1 });
    }
    const vis = w.visibility[this.player];
    const seen = (s: number) => vis?.[(w.y[s] >> FX_SHIFT) * w.size + (w.x[s] >> FX_SHIFT)] === 2;
    const r2 = sq(CAPTURE_DEFENCE_RADIUS);
    // Undefended as far as we can see: no visible enemy soldier or defence near it.
    const looksUndefended = (b: number) => {
      for (let s = 0; s < w.highSlot; s++) {
        if (s === b || !w.alive[s] || w.owner[s] < 0 || !w.areEnemies(this.player, w.owner[s]) || !seen(s)) continue;
        const k = KINDS[w.kind[s]];
        const defends = k.unit ? (k.unit.classes & C.Military) !== 0 : !!k.building && w.built[s] === 1 && ((k.building.classes & C.Tower) !== 0 || !!k.building.attack);
        if (defends && sq(w.x[s] - w.x[b]) + sq(w.y[s] - w.y[b]) <= r2) return false;
      }
      return true;
    };
    for (const e of v.engineers) {
      if (w.order[e] !== Order.None) continue;
      // Repair first: our own buildings below 70% health.
      let job = -1;
      let best = Infinity;
      for (const b of v.buildings) {
        if (!w.built[b] || w.hp[b] * 10 >= w.maxHp[b] * 7) continue;
        const d = sq(w.x[b] - w.x[e]) + sq(w.y[b] - w.y[e]);
        if (d < best) {
          best = d;
          job = b;
        }
      }
      if (job >= 0) {
        this.issue(sim, { t: 'repair', ids: [w.idOf(e)], target: w.idOf(job) });
        continue;
      }
      // Otherwise the nearest enemy building in sight, within 30 tiles, standing undefended.
      best = sq(30 * FX_ONE);
      for (let b = 0; b < w.highSlot; b++) {
        if (!canCapture(w, this.player, b) || !seen(b)) continue;
        const d = sq(w.x[b] - w.x[e]) + sq(w.y[b] - w.y[e]);
        if (d < best && looksUndefended(b)) {
          best = d;
          job = b;
        }
      }
      if (job >= 0) this.issue(sim, { t: 'capture', ids: [w.idOf(e)], target: w.idOf(job) });
    }
  }

  // ---------- Perception ----------
  private view(w: World) {
    const pid = this.player;
    const villagers: number[] = [];
    const military: number[] = [];
    const navy: number[] = [];
    const fishers: number[] = [];
    const scouts: number[] = [];
    const engineers: number[] = [];
    const buildings: number[] = [];
    const transports: number[] = [];
    const counts = new Map<string, number>();
    let tc = -1;
    // Soldiers inside a building or ship: out of reach of orders, but still part of the army.
    let aboard = 0;
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.owner[s] !== pid) continue;
      const k = KINDS[w.kind[s]];
      if (w.cat[s] === Cat.Held) {
        if (k.unit!.classes & C.Military) aboard++;
        continue;
      }
      if (k.unit) {
        if (k.unit.garrison) transports.push(s);
        else if (k.unit.classes & C.Ship) (k.unit.gatherRates ? fishers : navy).push(s);
        else if (k.unit.classes & C.Villager) villagers.push(s);
        else if (k.unit.classes & C.Scout) scouts.push(s);
        else if (k.unit.classes & C.Engineer) engineers.push(s);
        else military.push(s);
      } else if (k.building) {
        buildings.push(s);
        counts.set(k.key, (counts.get(k.key) ?? 0) + 1);
        if (k.key === 'townCenter' && tc < 0 && w.built[s]) tc = s;
      }
    }
    if (tc < 0 && buildings.length > 0) tc = buildings[0];
    // Visible enemy military near our buildings.
    let threat = -1;
    let threatD = Infinity;
    const vis = w.visibility[pid];
    for (const b of buildings) {
      w.forUnitsInRadius(w.x[b], w.y[b], 14 * FX_ONE, (o, d2) => {
        if (!w.areEnemies(pid, w.owner[o])) return;
        if (!(KINDS[w.kind[o]].unit!.classes & C.Military) || KINDS[w.kind[o]].unit!.classes & C.Scout) return;
        if (vis && vis[(w.y[o] >> FX_SHIFT) * w.size + (w.x[o] >> FX_SHIFT)] !== 2) return;
        if (d2 < threatD) {
          threatD = d2;
          threat = o;
        }
      });
      if (threat >= 0) break;
    }
    return { villagers, military, scouts, engineers, buildings, counts, tc, threat, navy, fishers, transports, aboard };
  }

  private observe(w: World): void {
    const vis = w.visibility[this.player];
    if (!vis) return;
    let cav = 0;
    let arc = 0;
    let inf = 0;
    let sie = 0;
    let shp = 0;
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.owner[s] < 0 || !w.areEnemies(this.player, w.owner[s])) continue;
      const vi = vis[(w.y[s] >> FX_SHIFT) * w.size + (w.x[s] >> FX_SHIFT)];
      if (w.cat[s] === Cat.Building) {
        if (vi > 0) this.known.set(w.idOf(s), { id: w.idOf(s), x: w.x[s], y: w.y[s], owner: w.owner[s] });
        continue;
      }
      if (vi !== 2) continue;
      const cl = KINDS[w.kind[s]].unit!.classes;
      if (!(cl & C.Military)) continue;
      if (cl & C.Ship) shp++;
      else if (cl & C.Siege) sie++;
      else if (cl & C.Cavalry) cav++;
      else if (cl & C.Archer) arc++;
      else inf++;
    }
    // Exponential memory of enemy composition.
    const e = this.enemySeen;
    e.cavalry = Math.max(cav, (e.cavalry * 9) / 10);
    e.archer = Math.max(arc, (e.archer * 9) / 10);
    e.infantry = Math.max(inf, (e.infantry * 9) / 10);
    e.siege = Math.max(sie, (e.siege * 9) / 10);
    e.ships = Math.max(shp, ((e.ships ?? 0) * 9) / 10);
    for (const [id, k] of this.known) {
      const s = w.resolve(id);
      const vi = vis[(k.y >> FX_SHIFT) * w.size + (k.x >> FX_SHIFT)];
      if (s < 0 && vi === 2) this.known.delete(id);
    }
  }

  /** Resources the AI is currently saving for (next age-up), or null. */
  private savingGoal(w: World, v: ReturnType<AIController['view']>): number[] | null {
    const p = w.players[this.player];
    // A Wonder victory is the bigger prize: save for it before the next age.
    if (this.wonderWanted) return p.stats[kindIndex('wonder')].cost;
    if (p.age >= this.maxAge(w)) return null;
    const ageing = v.buildings.some((b) => w.queues[b]?.some((it) => it.tech >= 0 && ALL_TECHS[it.tech].advancesTo !== undefined));
    if (ageing) return null;
    const cap = w.rules >= 7 ? this.villagerTarget(w) - 2 : this.d.maxVillagers - 2;
    const need = Math.min(cap, Math.trunc(([16, 24, 32, 40][p.age] * this.pp.ecoBias) / 100));
    if (v.villagers.length < need) return null;
    const ti = ALL_TECHS.findIndex((t) => t.advancesTo === p.age + 1);
    return p.techStats[ti].cost;
  }

  // ---------- Strategic: economy ----------
  private economy(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    // Desired gatherer split by age.
    const age = p.age;
    const ageing = v.buildings.some((b) => w.queues[b]?.some((it) => it.tech >= 0 && ALL_TECHS[it.tech].advancesTo !== undefined));
    const nextAgeNeedsGold = age >= 1 || ageing;
    const want = age === 0 ? (nextAgeNeedsGold ? [55, 30, 15, 0] : [62, 38, 0, 0]) : age === 1 ? [45, 32, 20, 3] : age === 2 ? [40, 27, 28, 5] : [38, 25, 30, 7];
    // Rules v7: castles, the later ages and the Wonder all need stone; keep more on it.
    if (w.rules >= 7 && age >= 2) want[3] += 5;
    // Shift gatherers toward the saving goal and away from stockpiles.
    const goal = this.savingGoal(w, v);
    for (let r = 0; r < 4; r++) {
      const stock = p.res[r] / FX_ONE;
      const need = goal ? goal[r] : 0;
      if (need > stock) want[r] += 15;
      // Stockpiles beyond what we are saving for pull gatherers away.
      const surplus = stock - need;
      if (surplus > 300) want[r] = Math.max(0, want[r] - Math.trunc(surplus / 300) * 15);
    }
    // Wood funds farms, houses and buildings: never let it dry up.
    if (p.res[1] < 150 * FX_ONE) want[1] += 12;
    const wsum = want[0] + want[1] + want[2] + want[3] || 1;
    for (let r = 0; r < 4; r++) want[r] = (want[r] * 100) / wsum;
    const have = [0, 0, 0, 0];
    const idle: number[] = [];
    for (const s of v.villagers) {
      if (w.order[s] === Order.Gather) {
        const t = w.resolve(w.target[s]);
        const r = t >= 0 ? resourceOf(w, t) : w.carryRes[s];
        if (r >= 0) have[r]++;
      } else if (w.order[s] === Order.None && w.state[s] === UState.Idle && !w.moving[s]) idle.push(s);
    }
    const total = v.villagers.length;
    // Move surplus gatherers off over-served resources (a few per decision to avoid thrashing).
    let moves = 0;
    for (let r = 0; r < 4 && moves < 4; r++) {
      while (moves < 4 && have[r] - (want[r] * total) / 100 >= 2) {
        const mover = v.villagers.find((s) => !idle.includes(s) && w.order[s] === Order.Gather && (() => {
          const t = w.resolve(w.target[s]);
          return t >= 0 && resourceOf(w, t) === r && (!isFarm(w, t) || p.res[r] > 700 * FX_ONE);
        })());
        if (mover === undefined) break;
        idle.push(mover);
        have[r]--;
        moves++;
      }
    }
    this.foodWanted = Math.round((want[0] * total) / 100);
    for (const s of idle) {
      // Pick most under-served resource.
      let best = 0;
      let bestGap = -Infinity;
      for (let r = 0; r < 4; r++) {
        const gap = (want[r] * total) / 100 - have[r];
        if (gap > bestGap) {
          bestGap = gap;
          best = r;
        }
      }
      const t = this.findResource(w, s, best, v.tc);
      if (t >= 0) {
        this.issue(sim, { t: 'gather', ids: [w.idOf(s)], target: w.idOf(t) });
        have[best]++;
      } else if (best === Res.Food) {
        // No natural food: build a farm.
        this.placeNear(sim, w, v, 'farm', [w.idOf(s)], 3);
        have[best]++;
      } else {
        const alt = this.findResource(w, s, Res.Wood, v.tc);
        if (alt >= 0) this.issue(sim, { t: 'gather', ids: [w.idOf(s)], target: w.idOf(alt) });
      }
    }
    // Train villagers.
    const tcs = v.buildings.filter((b) => KINDS[w.kind[b]].key === 'townCenter' && w.built[b]);
    const target = this.villagerTarget(w);
    for (const tc of tcs) {
      const q = w.queues[tc];
      const busyAge = q?.some((it) => it.tech >= 0 && ALL_TECHS[it.tech].advancesTo !== undefined);
      if (total < target && (!q || q.length < (goal ? 1 : 2)) && !busyAge && p.res[0] >= 50 * FX_ONE && p.pop < p.popCap) {
        this.issue(sim, { t: 'train', building: w.idOf(tc), kind: 'villager', count: 1 });
      }
    }
  }

  /** Docks, fishing fleets and a small navy where water is useful. */
  private naval(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    for (const s of v.fishers) {
      if (w.order[s] !== Order.None || w.moving[s]) continue;
      const t = this.findResource(w, s, Res.Food, v.tc);
      if (t >= 0) this.issue(sim, { t: 'gather', ids: [w.idOf(s)], target: w.idOf(t) });
    }
    const docks = v.buildings.filter((b) => KINDS[w.kind[b]].key === 'dock' && w.built[b]);
    if (!docks.length) return;
    let fish = 0;
    w.forStaticInRadius(w.x[docks[0]], w.y[docks[0]], 20 * FX_ONE, (o) => {
      if (KINDS[w.kind[o]].resource?.model === 'fish') fish++;
    });
    const dock = docks[0];
    const q = w.queues[dock];
    if (q && q.length >= 2) return;
    if (v.fishers.length < Math.min(8, fish * 2) && p.res[1] >= 150 * FX_ONE) {
      this.issue(sim, { t: 'train', building: w.idOf(dock), kind: 'fishingShip', count: 1 });
      return;
    }
    // Rules v8: a transport when an enemy is best reached by sea.
    const queuedShip = (q ?? []).some((it) => it.kind === kindIndex('caravelle'));
    if (w.rules >= 8 && p.age >= 1 && !v.transports.length && !queuedShip && this.wantsTransport(w, v)) {
      const ki = kindIndex('caravelle');
      if (canAfford(p, p.stats[ki].cost)) this.issue(sim, { t: 'train', building: w.idOf(dock), kind: 'caravelle', count: 1 });
      return;
    }
    const want = this.enemySeen.ships > 0 ? 3 + Math.ceil(this.enemySeen.ships * 1.2) : 2;
    if (v.navy.length < want) {
      const opts = trainableAt(w, p, dock).filter((k) => KINDS[k].unit!.attack && KINDS[k].unit!.age <= p.age);
      const best = opts.sort((a, b) => KINDS[b].unit!.age - KINDS[a].unit!.age)[0];
      if (best !== undefined && canAfford(p, p.stats[best].cost)) this.issue(sim, { t: 'train', building: w.idOf(dock), kind: KINDS[best].key, count: 1 });
    }
  }

  private findResource(w: World, s: number, r: number, tc: number): number {
    let best = -1;
    let bestD = Infinity;
    const vis = w.visibility[this.player];
    const ox = tc >= 0 ? w.x[tc] : w.x[s];
    const oy = tc >= 0 ? w.y[tc] : w.y[s];
    w.forStaticInRadius(ox, oy, 26 * FX_ONE, (o, d2) => {
      if (resourceOf(w, o) !== r) return;
      if (!gatherableBy(w, s, o)) return;
      if (w.isUnreachable(this.player, w.idOf(o))) return;
      if (w.cat[o] === Cat.Building) {
        // The AI keeps one farmer per farm for efficiency (players may stack more).
        if (w.owner[o] !== this.player || w.resolve(w.worker[o]) >= 0) return;
      } else if (vis && vis[(w.y[o] >> FX_SHIFT) * w.size + (w.x[o] >> FX_SHIFT)] === 0) return;
      // Avoid resources near enemies.
      if (d2 < bestD || (d2 === bestD && o < best)) {
        bestD = d2;
        best = o;
      }
    });
    return best;
  }

  // ---------- Strategic: construction ----------
  private build(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    const builders = v.villagers.filter((s) => w.order[s] === Order.Build);
    const unfinished = v.buildings.filter((b) => !w.built[b] && !isFarm(w, b));
    // Abandon foundations nobody can reach.
    for (const b of unfinished) {
      if (w.isUnreachable(this.player, w.idOf(b))) this.issue(sim, { t: 'delete', ids: [w.idOf(b)] });
    }
    // Help finish foundations (a Wonder gets a crew of six under rules v7).
    for (const b of unfinished) {
      let n = 0;
      for (const s of builders) if (w.resolve(w.target[s]) === b) n++;
      if (w.rules >= 7 && KINDS[w.kind[b]].key === 'wonder' && n < 6) {
        const crew: number[] = [];
        for (const s of v.villagers) if (crew.length < 6 - n && w.order[s] !== Order.Build) crew.push(w.idOf(s));
        if (crew.length) this.issue(sim, { t: 'repair', ids: crew, target: w.idOf(b) });
        continue;
      }
      if (n === 0) {
        const vil = this.pickBuilder(w, v, w.x[b], w.y[b]);
        if (vil >= 0) this.issue(sim, { t: 'repair', ids: [w.idOf(vil)], target: w.idOf(b) });
      }
    }
    if (unfinished.length >= 2) return;
    const c = (k: string) => v.counts.get(k) ?? 0;
    if (this.wonderWanted && this.tryBuild(sim, w, v, 'wonder', 5, 6)) return;
    const housing = p.popCap - p.pop;
    const prodBuildings = c('barracks') + c('archeryRange') + c('stable') + c('townCenter');
    const houseInProgress = unfinished.some((b) => KINDS[w.kind[b]].key === 'house');
    if (p.popCap < 200 && housing < 3 + prodBuildings * 2 && (!houseInProgress || housing <= 0)) {
      if (this.tryBuild(sim, w, v, 'house', 2, 4)) return;
    }
    const nVil = v.villagers.length;
    // Drop-off camps near resources far from the town centre.
    if (c('lumberCamp') < 1 + Math.trunc(nVil / 25) && nVil >= 6) {
      const t = v.tc >= 0 ? this.findResource(w, v.tc, Res.Wood, v.tc) : -1;
      if (t >= 0 && w.edgeDist(v.tc, w.x[t], w.y[t]) > 5 * FX_ONE && this.buildAt(sim, w, v, 'lumberCamp', w.x[t], w.y[t])) return;
    }
    if (c('miningCamp') < 1 && nVil >= 10) {
      const t = v.tc >= 0 ? this.findResource(w, v.tc, Res.Gold, v.tc) : -1;
      if (t >= 0 && this.buildAt(sim, w, v, 'miningCamp', w.x[t], w.y[t])) return;
    }
    if (c('mill') < 1 && nVil >= 8) {
      const t = v.tc >= 0 ? this.findResource(w, v.tc, Res.Food, v.tc) : -1;
      if (t >= 0 && w.cat[t] === Cat.Resource && this.buildAt(sim, w, v, 'mill', w.x[t], w.y[t])) return;
    }
    // Farms: as natural food near the town centre runs low, lay farms ahead of need.
    if (v.tc >= 0 && nVil >= 8) {
      let natural = 0;
      w.forStaticInRadius(w.x[v.tc], w.y[v.tc], 16 * FX_ONE, (o) => {
        if (w.cat[o] === Cat.Resource && resourceOf(w, o) === Res.Food) natural += w.resAmt[o] / FX_ONE;
      });
      const farms = c('farm');
      const naturalWorkers = Math.trunc(natural / 150);
      const farmsInProgress = unfinished.length + v.buildings.filter((b) => !w.built[b] && isFarm(w, b)).length;
      if (farms + naturalWorkers < this.foodWanted && farmsInProgress < 2 && p.res[1] >= (c('lumberCamp') ? 80 : 160) * FX_ONE) {
        const vil = this.pickBuilder(w, v, w.x[v.tc], w.y[v.tc]);
        if (vil >= 0 && this.placeNear(sim, w, v, 'farm', [w.idOf(vil)], 3)) return;
      }
    }
    if (c('dock') < 1 && nVil >= 10 && v.tc >= 0) {
      let fish = 0;
      w.forStaticInRadius(w.x[v.tc], w.y[v.tc], 22 * FX_ONE, (o) => {
        if (KINDS[w.kind[o]].resource?.model === 'fish') fish++;
      });
      // Rules v8: also when an enemy is best reached by sea (for a transport).
      const sea = w.rules >= 8 && p.age >= 1 && this.wantsTransport(w, v);
      if ((fish >= 2 || sea) && this.tryBuild(sim, w, v, 'dock', 3, 4)) return;
    }
    const towers = this.pp.towers + this.mods(w).towersPlus;
    if (w.rules >= 7 && p.age === 0 && towers > 0 && c('watchtower') < Math.min(2, towers) && nVil >= 10 && this.tryBuild(sim, w, v, 'watchtower', 1)) return;
    const aggressiveEarly = this.pp.aggression < 100;
    if (c('barracks') < 1 && nVil >= (aggressiveEarly ? 8 : 12)) {
      if (this.tryBuild(sim, w, v, 'barracks', 3)) return;
    }
    if (p.age >= 1) {
      if (c('blacksmith') < 1 && nVil >= 18 && this.tryBuild(sim, w, v, 'blacksmith', 3)) return;
      if (c('archeryRange') < 1 && this.tryBuild(sim, w, v, 'archeryRange', 3)) return;
      if (c('stable') < 1 && p.stats[kindIndex('stable')].enabled && this.tryBuild(sim, w, v, 'stable', 3)) return;
      if (c('market') < 1 && nVil >= 25 && this.tryBuild(sim, w, v, 'market', 3)) return;
      if (c('watchtower') + c('guardTower') < towers && this.tryBuild(sim, w, v, 'guardTower', 1)) return;
    }
    if (p.age >= 2) {
      if (c('university') < 1 && nVil >= this.vil(w, 28, 0.55) && this.tryBuild(sim, w, v, 'university', 3)) return;
      if (c('siegeWorkshop') < 1 && this.tryBuild(sim, w, v, 'siegeWorkshop', 3)) return;
      if (c('castle') < 1 && p.res[3] >= 650 * FX_ONE && this.tryBuild(sim, w, v, 'castle', 4)) return;
      if (c('monastery') < 1 && nVil >= this.vil(w, 35, 0.65) && this.tryBuild(sim, w, v, 'monastery', 3)) return;
      if (c('townCenter') < 1 + this.pp.expand + this.mods(w).expandPlus && nVil >= this.vil(w, 30, 0.7) && this.tryBuild(sim, w, v, 'townCenter', 4, 16)) return;
      const extra = Math.trunc(nVil / 20);
      if (c('barracks') + c('archeryRange') + c('stable') < 3 + extra && p.res[1] > 400 * FX_ONE) {
        const opts = ['archeryRange', 'stable', 'barracks'].filter((k) => p.stats[kindIndex(k)].enabled);
        if (this.tryBuild(sim, w, v, opts[this.rng.int(opts.length)], 3)) return;
      }
    }
    if (p.age >= 4) {
      if (c('arsenal') < 1 && p.stats[kindIndex('arsenal')].enabled && this.tryBuild(sim, w, v, 'arsenal', 3)) return;
      if (c('foundry') < 1 && p.stats[kindIndex('foundry')].enabled && this.tryBuild(sim, w, v, 'foundry', 3)) return;
      // Rules v7: the industrial economy too (drop-off for every resource, more housing, late techs).
      if (w.rules >= 7) {
        if (c('logisticsDepot') < 1 && this.tryBuild(sim, w, v, 'logisticsDepot', 3)) return;
        if (c('industrialWorkshop') < 1 && this.tryBuild(sim, w, v, 'industrialWorkshop', 3)) return;
      }
    }
    // Age advancement prerequisites: make sure we have enough qualifying buildings.
    if (p.age < this.maxAge(w)) {
      const req = AGE_UP_REQUIREMENTS[p.age + 1];
      let have = 0;
      for (const k of req.from) if ((v.counts.get(k) ?? 0) > 0) have++;
      if (have < req.count && nVil >= 14) {
        for (const k of req.from) {
          if ((v.counts.get(k) ?? 0) > 0) continue;
          const ki = kindIndex(k);
          if (!p.stats[ki].enabled || KINDS[ki].building!.age > p.age) continue;
          if (this.tryBuild(sim, w, v, k, KINDS[ki].building!.size)) return;
        }
      }
    }
  }

  private pickBuilder(w: World, v: ReturnType<AIController['view']>, x: number, y: number): number {
    let best = -1;
    let bestD = Infinity;
    for (const s of v.villagers) {
      if (w.order[s] === Order.Build) continue;
      const d = (w.x[s] - x) * (w.x[s] - x) + (w.y[s] - y) * (w.y[s] - y);
      // Prefer wood gatherers and idle villagers.
      const t = w.resolve(w.target[s]);
      const pen = w.order[s] === Order.None ? 0 : t >= 0 && resourceOf(w, t) === Res.Wood ? 4 * FX_ONE * FX_ONE : 40 * FX_ONE * FX_ONE;
      if (d + pen < bestD) {
        bestD = d + pen;
        best = s;
      }
    }
    return best;
  }

  private tryBuild(sim: Simulation, w: World, v: ReturnType<AIController['view']>, key: string, size: number, minR = 3): boolean {
    if (v.tc < 0) return false;
    const p = w.players[this.player];
    const ki = kindIndex(key);
    if (!p.stats[ki].enabled || p.age < KINDS[ki].building!.age) return false;
    if (!canAfford(p, p.stats[ki].cost)) return false;
    const b = this.pickBuilder(w, v, w.x[v.tc], w.y[v.tc]);
    if (b < 0) return false;
    return this.placeNear(sim, w, v, key, [w.idOf(b)], size, minR);
  }

  private buildAt(sim: Simulation, w: World, v: ReturnType<AIController['view']>, key: string, x: number, y: number): boolean {
    const p = w.players[this.player];
    const ki = kindIndex(key);
    if (!canAfford(p, p.stats[ki].cost)) return false;
    const b = this.pickBuilder(w, v, x, y);
    if (b < 0) return false;
    const size = KINDS[ki].building!.size;
    const spot = this.findSpot(w, ki, x >> FX_SHIFT, y >> FX_SHIFT, size, 1, 6);
    if (!spot) return false;
    this.issue(sim, { t: 'build', ids: [w.idOf(b)], kind: key, tx: spot[0], ty: spot[1] });
    return true;
  }

  private placeNear(sim: Simulation, w: World, v: ReturnType<AIController['view']>, key: string, ids: number[], size: number, minR = 3): boolean {
    if (v.tc < 0) return false;
    const p = w.players[this.player];
    const ki = kindIndex(key);
    if (!canAfford(p, p.stats[ki].cost)) return false;
    const cx = w.x[v.tc] >> FX_SHIFT;
    const cy = w.y[v.tc] >> FX_SHIFT;
    const spot = this.findSpot(w, ki, cx, cy, size, minR, key === 'farm' ? 9 : 20);
    if (!spot) return false;
    this.issue(sim, { t: 'build', ids, kind: key, tx: spot[0], ty: spot[1] });
    return true;
  }

  /** Spiral search for a valid placement leaving a one-tile gap around buildings. */
  private findSpot(w: World, ki: number, cx: number, cy: number, size: number, minR: number, maxR: number): [number, number] | null {
    const farm = KINDS[ki].building!.foodAmount !== undefined;
    for (let r = minR; r <= maxR; r++) {
      // Rotate start of ring per call for variety, deterministically.
      const start = this.rng.int(8 * r);
      const ring: [number, number][] = [];
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) if (Math.max(Math.abs(dx), Math.abs(dy)) === r) ring.push([dx, dy]);
      for (let i = 0; i < ring.length; i++) {
        const [dx, dy] = ring[(i + start) % ring.length];
        const tx = cx + dx - (size >> 1);
        const ty = cy + dy - (size >> 1);
        if (!canPlace(w, ki, tx, ty, this.player)) continue;
        if (!farm && !this.hasGap(w, tx, ty, size)) continue;
        return [tx, ty];
      }
    }
    return null;
  }

  private hasGap(w: World, tx: number, ty: number, size: number): boolean {
    for (let y = ty - 1; y <= ty + size; y++)
      for (let x = tx - 1; x <= tx + size; x++) {
        if (x < 0 || y < 0 || x >= w.size || y >= w.size) return false;
        const o = w.occupant[y * w.size + x];
        if (o >= 0) {
          const s = w.resolve(o);
          if (s >= 0 && w.cat[s] === Cat.Building) return false;
        }
      }
    return true;
  }

  // ---------- Strategic: research ----------
  private research(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    const nVil = v.villagers.length;
    const goal = this.savingGoal(w, v);
    for (const b of v.buildings) {
      if (!w.built[b]) continue;
      const q = w.queues[b];
      const qlen = q ? q.length : 0;
      for (const ti of researchableAt(w, p, b)) {
        const t = ALL_TECHS[ti];
        // Age-ups may queue behind one unit; other research needs an idle building.
        if (qlen > (t.advancesTo !== undefined ? 1 : 0)) continue;
        if (techAvailability(w, p, ti) !== null) continue;
        const cost = p.techStats[ti].cost;
        if (t.advancesTo !== undefined) {
          if (t.advancesTo > this.maxAge(w)) continue;
          const vilNeed = Math.trunc(([0, 16, 24, 32, 40][t.advancesTo] * this.pp.ecoBias) / 100);
          if (nVil < Math.min(vilNeed, (w.rules >= 7 ? this.villagerTarget(w) : this.d.maxVillagers) - 2)) continue;
        } else {
          const eco = t.effects.some((e) => e.stat === 'gatherPct' || e.stat === 'farmFood' || e.stat === 'carry');
          if (goal) {
            let ok = eco;
            for (let r = 0; r < 4; r++) if (cost[r] > 0 && p.res[r] < (cost[r] + goal[r]) * FX_ONE) ok = false;
            if (!ok) continue;
            if (!canAfford(p, cost)) continue;
            this.issue(sim, { t: 'research', building: w.idOf(b), tech: t.key });
            return;
          }
          // Keep a reserve for age-ups and units; tech personality spends more freely.
          const reserve = Math.trunc((300 * 100) / this.pp.tech);
          let ok = true;
          for (let r = 0; r < 4; r++) if (cost[r] > 0 && p.res[r] < (cost[r] + reserve) * FX_ONE) ok = false;
          if (!ok) continue;
        }
        if (!canAfford(p, cost)) continue;
        this.issue(sim, { t: 'research', building: w.idOf(b), tech: t.key });
        return;
      }
    }
  }

  /** Convert large surpluses into scarce resources at the market. */
  private trade(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    if ((v.counts.get('market') ?? 0) === 0) return;
    const gold = p.res[2] / FX_ONE;
    const res = [0, 1, 3].map((r) => ({ r, stock: p.res[r] / FX_ONE }));
    const poorest = res.reduce((a, b) => (a.stock < b.stock ? a : b));
    const richest = res.reduce((a, b) => (a.stock > b.stock ? a : b));
    if (gold > 700 && poorest.stock < 150 && p.prices[poorest.r] < 220) this.issue(sim, { t: 'trade', res: poorest.r, buy: true });
    else if (richest.stock > 1200 && gold < 300) this.issue(sim, { t: 'trade', res: richest.r, buy: false });
  }

  // ---------- Strategic: army composition ----------
  private train(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const p = w.players[this.player];
    if (p.pop >= p.popCap) return;
    const ageUp = v.buildings.some((b) => w.queues[b]?.some((it) => it.tech >= 0 && ALL_TECHS[it.tech].advancesTo !== undefined));
    // Save for age-up when close, unless under threat.
    const goal = ageUp ? null : this.savingGoal(w, v);
    const e = this.enemySeen;
    const threatened = v.threat >= 0;
    const armyCap = p.age === 0 && !threatened ? (this.pp.aggression < 100 ? 8 : 3) : 999;
    if (v.military.length + v.aboard >= armyCap) return;
    for (const b of v.buildings) {
      if (!w.built[b] || KINDS[w.kind[b]].key === 'townCenter') continue;
      const q = w.queues[b];
      if (q && q.length >= 2) continue;
      // Engineers are not soldiers: they are trained and used by engineerWork.
      const opts = trainableAt(w, p, b).filter((k) => KINDS[k].unit!.age <= p.age && !(KINDS[k].unit!.classes & (C.Ship | C.Engineer)));
      if (opts.length === 0) continue;
      let pick = -1;
      let bestScore = -Infinity;
      for (const k of opts) {
        const u = KINDS[k].unit!;
        const cost = p.stats[k].cost;
        const minArmy = 4 + p.age * 5;
        if (goal && !threatened && v.military.length >= minArmy) {
          let ok = true;
          for (let r = 0; r < 4; r++) if (cost[r] > 0 && p.res[r] < (goal[r] + cost[r]) * FX_ONE) ok = false;
          if (!ok) continue;
        }
        if (!canAfford(p, cost)) continue;
        let score = u.age * 10 + (u.civ ? 15 : 0) + this.rng.int(8);
        if (u.classes & C.Scout) score -= 40;
        if (u.classes & C.Siege) score += this.attackWave > 0 ? 5 : -30;
        if (w.rules >= 7) {
          // A few monks to heal the army, not a monastery's worth.
          if (u.classes & C.Healer && v.military.filter((s) => KINDS[w.kind[s]].unit!.classes & C.Healer).length >= 3) continue;
          const m = this.mods(w);
          if (u.classes & C.Siege) score += m.siegePlus;
          if (this.d.counters || m.counterPct > 100) {
            const k = m.counterPct / 100;
            if (u.classes & C.Spear) score += Math.trunc(e.cavalry * 4 * k);
            if (u.classes & C.Archer && !(u.classes & C.Cavalry)) score += Math.trunc((e.infantry * 3 - e.cavalry * 2) * k);
            if (u.classes & C.Cavalry) score += Math.trunc((e.archer * 3 + e.siege * 4 - e.infantry * 1) * k);
            if (u.classes & C.Gunpowder) score += 6;
          }
        } else if (this.d.counters) {
          if (u.classes & C.Spear) score += e.cavalry * 4;
          if (u.classes & C.Archer && !(u.classes & C.Cavalry)) score += e.infantry * 3 - e.cavalry * 2;
          if (u.classes & C.Cavalry) score += e.archer * 3 + e.siege * 4 - e.infantry * 1;
          if (u.classes & C.Gunpowder) score += 6;
        }
        if (score > bestScore) {
          bestScore = score;
          pick = k;
        }
      }
      if (pick >= 0) this.issue(sim, { t: 'train', building: w.idOf(b), kind: KINDS[pick].key, count: 1 });
    }
  }

  // ---------- Operational ----------
  private operations(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    // Scouting: visit ring waypoints then enemy start candidates.
    for (const s of v.scouts) {
      if (w.stance[s] !== 3) this.issue(sim, { t: 'stance', ids: [w.idOf(s)], stance: 'passive' });
      if (w.order[s] !== Order.None || w.moving[s]) continue;
      const pts = this.scoutPoints(w, v);
      const [x, y] = pts[this.scoutIdx++ % pts.length];
      this.issue(sim, { t: 'move', ids: [w.idOf(s)], x, y });
    }
    const threat = v.threat;
    // Rules v7: an army mostly lost while defending is a lost battle too.
    if (w.rules >= 7) {
      if (threat >= 0) {
        this.defencePeak = Math.max(this.defencePeak, v.military.length);
        if (this.defencePeak >= 6 && v.military.length * 2 < this.defencePeak) {
          this.rethink(w);
          this.defencePeak = v.military.length;
        }
      } else this.defencePeak = 0;
    }
    const idleArmy = v.military.filter((s) => w.order[s] === Order.None || (w.order[s] === Order.AttackMove && !this.attacking));
    if (threat >= 0) {
      const ids = v.military.filter((s) => w.order[s] !== Order.Attack).map((s) => w.idOf(s));
      if (ids.length > 0) this.issue(sim, { t: 'move', ids: ids.slice(0, 200), x: w.x[threat], y: w.y[threat], attackMove: true });
      this.attacking = false;
      return;
    }
    let threshold = Math.max(4, Math.trunc((this.d.attackAt * this.pp.aggression) / 100) + this.attackWave * 4);
    let waveGap = 20 * this.d.waveGap;
    if (w.rules >= 7) {
      const m = this.mods(w);
      threshold = Math.trunc((threshold * m.armyPct) / 100);
      waveGap = Math.trunc((waveGap * m.waveGapPct) / 100);
      // An enemy Wonder must fall before its timer runs out: go now, with what there is.
      if (this.enemyWonder >= 0) {
        threshold = Math.max(4, threshold >> 1);
        waveGap = 20 * 30;
      }
    }
    // Rules v7: with our own Wonder standing (or rising), the army stays home to guard it.
    const guardWonder = w.rules >= 7 && (v.counts.get('wonder') ?? 0) > 0 && this.enemyWonder < 0;
    if (!this.attacking && !guardWonder && v.military.length >= threshold && w.tick - this.lastAttackTick > waveGap) {
      this.waveTarget = -1;
      const tgt = this.pickTarget(w, v);
      if (tgt) {
        this.attacking = true;
        this.lastAttackTick = w.tick;
        this.attackWave++;
        this.seaRoute.clear();
        const sea = w.rules >= 8 ? this.embark(sim, w, v, v.military, tgt[0], tgt[1]) : [];
        const ids = v.military.filter((s) => !sea.includes(s)).map((s) => w.idOf(s));
        if (ids.length) this.issue(sim, { t: 'move', ids: ids.slice(0, 200), x: tgt[0], y: tgt[1], attackMove: true, formation: ids.length > 8 ? 'line' : 'none' });
      }
    } else if (this.attacking) {
      // Keep the wave going: idle units at destination push to next target; retreat if crushed.
      if (v.military.length + v.aboard < Math.max(3, threshold >> 2)) {
        // The wave was crushed: a lost battle.
        this.rethink(w);
        this.attacking = false;
        const ids = v.military.map((s) => w.idOf(s));
        if (ids.length) this.issue(sim, { t: 'retreat', ids });
        return;
      }
      if (idleArmy.length > 0) {
        // Arrived and nothing of the target's is known: look for another enemy instead of waiting there.
        if (w.rules >= 4 && this.waveTarget >= 0 && !this.knowsBuildingOf(this.waveTarget)) this.waveTarget = -1;
        const tgt = this.pickTarget(w, v);
        if (tgt) this.issue(sim, { t: 'move', ids: idleArmy.map((s) => w.idOf(s)).slice(0, 200), x: tgt[0], y: tgt[1], attackMove: true });
        else this.attacking = false;
      }
    } else if (guardWonder && idleArmy.length > 0) {
      const wonder = v.buildings.find((b) => KINDS[w.kind[b]].key === 'wonder')!;
      const far = idleArmy.filter((s) => sq(w.x[s] - w.x[wonder]) + sq(w.y[s] - w.y[wonder]) > sq(8 * FX_ONE));
      if (far.length) this.issue(sim, { t: 'move', ids: far.map((s) => w.idOf(s)), x: w.x[wonder], y: w.y[wonder] + 5 * FX_ONE, formation: 'box' });
    } else if (idleArmy.length > 0 && v.tc >= 0) {
      // Rally idle army near the town centre facing the map centre.
      const c = w.size * (FX_ONE >> 1);
      const gx = w.x[v.tc] + Math.trunc((c - w.x[v.tc]) / 5);
      const gy = w.y[v.tc] + Math.trunc((c - w.y[v.tc]) / 5);
      const far = idleArmy.filter((s) => sq(w.x[s] - gx) + sq(w.y[s] - gy) > sq(6 * FX_ONE));
      if (far.length) this.issue(sim, { t: 'move', ids: far.map((s) => w.idOf(s)), x: gx, y: gy, formation: 'box' });
    }
  }

  private scoutPoints(w: World, v: ReturnType<AIController['view']>): [number, number][] {
    const pts: [number, number][] = [];
    const cx = v.tc >= 0 ? w.x[v.tc] : w.size * 512;
    const cy = v.tc >= 0 ? w.y[v.tc] : w.size * 512;
    const r = 14 * FX_ONE;
    const dirs = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    for (const [dx, dy] of dirs) pts.push([clamp(w, cx + dx * r), clamp(w, cy + dy * r)]);
    const n = w.map.starts.length;
    for (let k = 0; k < n; k++) {
      // Rules v4: each computer scouts the other starts beginning after its own seat, so no seat (the
      // human is always seat 0 in single player) is everyone's first discovery.
      const i = w.rules >= 4 ? (this.player + 1 + k) % n : k;
      if (i === this.player) continue;
      const s = w.map.starts[i];
      pts.push([(s.tx << FX_SHIFT) + 512, (s.ty << FX_SHIFT) + 512]);
    }
    return pts;
  }

  private knowsBuildingOf(player: number): boolean {
    for (const k of this.known.values()) if (k.owner === player) return true;
    return false;
  }

  /**
   * Rules v4: each attack wave picks its target player at random among the living enemies, every one
   * equally likely, human or computer; the wave then stays on that player. Before, every wave went to
   * the nearest building the AI knew of, and since every AI scouted seat 0 first that was nearly
   * always the human. The choice comes from a hash of the match and wave, so replays stay exact.
   */
  private pickFairTarget(w: World, v: ReturnType<AIController['view']>): [number, number] | null {
    const alive = (i: number) => i !== this.player && i < w.players.length && !w.players[i].defeated && w.areEnemies(this.player, i);
    // Rules v7: an enemy Wonder comes first; aim at it if we have seen it, else at that player's town.
    if (this.enemyWonder >= 0) {
      this.waveTarget = this.enemyWonder;
      for (const k of this.known.values()) {
        const s = w.resolve(k.id);
        if (k.owner === this.enemyWonder && s >= 0 && KINDS[w.kind[s]].key === 'wonder') return [k.x, k.y];
      }
    }
    if (this.waveTarget < 0 || !alive(this.waveTarget)) {
      const enemies: number[] = [];
      for (let i = 0; i < w.players.length; i++) if (alive(i)) enemies.push(i);
      if (!enemies.length) return null;
      // After a wave found nothing at its target, prefer enemies whose buildings are known.
      const known = enemies.filter((i) => this.knowsBuildingOf(i));
      const pool = this.attacking && known.length ? known : enemies;
      this.waveTarget = pool[hash2(this.attackWave * 977 + this.player, w.tick, w.map.seed) % pool.length];
    }
    const ox = v.tc >= 0 ? w.x[v.tc] : w.size * 512;
    const oy = v.tc >= 0 ? w.y[v.tc] : w.size * 512;
    let best: KnownBuilding | null = null;
    let bestD = Infinity;
    for (const k of this.known.values()) {
      if (k.owner !== this.waveTarget) continue;
      const d = sq(k.x - ox) + sq(k.y - oy);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    if (best) return [best.x, best.y];
    const s = w.map.starts[this.waveTarget];
    return s ? [(s.tx << FX_SHIFT) + 512, (s.ty << FX_SHIFT) + 512] : null;
  }

  private pickTarget(w: World, v: ReturnType<AIController['view']>): [number, number] | null {
    if (w.rules >= 4) return this.pickFairTarget(w, v);
    const ox = v.tc >= 0 ? w.x[v.tc] : w.size * 512;
    const oy = v.tc >= 0 ? w.y[v.tc] : w.size * 512;
    let best: KnownBuilding | null = null;
    let bestD = Infinity;
    for (const k of this.known.values()) {
      if (w.players[k.owner]?.defeated) continue;
      const d = sq(k.x - ox) + sq(k.y - oy);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    if (best) return [best.x, best.y];
    // Unknown: probe candidate start locations (map layout is public knowledge).
    let bs: [number, number] | null = null;
    bestD = Infinity;
    for (let i = 0; i < w.map.starts.length; i++) {
      if (i === this.player || i >= w.players.length || w.players[i].defeated || !w.areEnemies(this.player, i)) continue;
      const s = w.map.starts[i];
      const x = (s.tx << FX_SHIFT) + 512;
      const y = (s.ty << FX_SHIFT) + 512;
      const d = sq(x - ox) + sq(y - oy);
      if (d < bestD) {
        bestD = d;
        bs = [x, y];
      }
    }
    return bs;
  }

  // ---------- Tactical ----------
  private tactics(sim: Simulation, w: World, v: ReturnType<AIController['view']>): void {
    const retreat: number[] = [];
    const focus = new Map<number, number[]>();
    for (const s of v.military) {
      if (w.order[s] !== Order.Attack && w.order[s] !== Order.AttackMove) continue;
      const hpPct = (w.hp[s] * 100) / w.maxHp[s];
      if (this.difficulty === 'expert' && hpPct < 20 && w.tick - w.lastHurtTick[s] < 20) {
        retreat.push(w.idOf(s));
        continue;
      }
      const u = KINDS[w.kind[s]].unit!;
      if (!u.attack || u.attack.range <= 0) continue;
      // Ranged focus fire: the weakest enemy in range.
      const st = w.players[this.player].stats[w.kind[s]];
      let weakest = -1;
      let weakHp = Infinity;
      w.forUnitsInRadius(w.x[s], w.y[s], st.range + FX_ONE, (o) => {
        if (!w.areEnemies(this.player, w.owner[o])) return;
        if (w.hp[o] < weakHp || (w.hp[o] === weakHp && o < weakest)) {
          weakHp = w.hp[o];
          weakest = o;
        }
      });
      if (weakest >= 0 && w.resolve(w.target[s]) !== weakest) {
        const id = w.idOf(weakest);
        const l = focus.get(id) ?? [];
        l.push(w.idOf(s));
        focus.set(id, l);
      }
    }
    if (retreat.length) this.issue(sim, { t: 'retreat', ids: retreat });
    for (const [t, ids] of focus) this.issue(sim, { t: 'attack', ids, target: t });
  }
}

function clamp(w: World, v: number): number {
  return Math.max(FX_ONE, Math.min((w.size - 1) * FX_ONE, v));
}

const sq = (v: number): number => v * v;
